import { Button, cn, Trash } from "@gearbox-protocol/deploy-tools-ui";
import { Download, Eye } from "lucide-react";
import { type FC, useCallback, useState } from "react";
import type { Accept, FileWithPath } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { downloadFile, formatBytes } from "utils";

import { FileViewDialog } from "./FileViewDialog";

const DEFAULT_ACCEPT: Accept = {
  "application/json": [".json"],
  "application/yaml": [".yaml", ".yml"],
};

export interface FileUploadProps {
  files?: File[];
  onChange: (files: File[]) => void;
  accept?: Accept;
  label?: string;
  className?: string;
}

export const FileUpload: FC<FileUploadProps> = props => {
  const {
    files = [],
    onChange,
    accept = DEFAULT_ACCEPT,
    label = "Upload some files here",
    className,
  } = props;
  const [viewed, setViewed] = useState(-1);

  const onDropAccepted = useCallback(
    (added: FileWithPath[]) => {
      // re-uploading a file replaces the previous version instead of adding a duplicate
      const replaced = new Set(added.map(f => f.name));
      onChange([...files.filter(f => !replaced.has(f.name)), ...added]);
    },
    [files, onChange],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted,
    accept,
  });

  const viewedFile = files[viewed];

  return (
    <div className={cn("flex flex-col gap-y-1", className)}>
      <div
        {...getRootProps({
          className:
            "dropzone border-2 border-dashed border-slate-500 rounded text-slate-500 p-4",
        })}
      >
        <input {...getInputProps()} />
        <p>{label}</p>
      </div>

      {files.map((file, index) => (
        <div key={file.name} className="flex flex-row items-center gap-1">
          <span>
            {file.name}{" "}
            <span className="text-xs text-slate-500">
              ({formatBytes(file.size)})
            </span>
          </span>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setViewed(index)}
          >
            <Eye size={16} />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => downloadFile(file)}
          >
            <Download size={16} />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange(files.filter((_, i) => i !== index))}
          >
            <Trash />
          </Button>
        </div>
      ))}

      {!!viewedFile && (
        <FileViewDialog file={viewedFile} onClose={() => setViewed(-1)} />
      )}
    </div>
  );
};
